import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { actualizarRopa } from '../../services/apis';
import { ImagenPrivada } from '../../utils/ImagenPrivada';

export const FormEditarRopa = ({ ropa, onClose, onSaved, mostrarEliminades }) => {
    const fileInputRef = useRef(null);
    const [nombre, setNombre] = useState('');
    const [color, setColor] = useState('#000000');
    const [foto, setFoto] = useState(null);
    const [preview, setPreview] = useState('');
    const [quitarFoto, setQuitarFoto] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!ropa) return;
        setNombre(ropa.nombre || '');
        setColor(ropa.color || '#000000');
        setFoto(null);
        setQuitarFoto(false);
        setError('');
    }, [ropa]);

    useEffect(() => {
        if (!foto) {
            setPreview('');
            return undefined;
        }
        const url = URL.createObjectURL(foto);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [foto]);

    const cerrar = () => {
        if (!guardando) onClose();
    };

    const guardar = async () => {
        if (!nombre.trim()) {
            setError('Indica el nom de la peça.');
            return;
        }

        const formData = new FormData();
        formData.append('accion', 'editar');
        formData.append('id', ropa.id);
        formData.append('nombre', nombre.trim());
        formData.append('color', color);
        formData.append('quitar_foto', quitarFoto ? '1' : '0');
        formData.append('mostrar_eliminadas', mostrarEliminades ? '1' : '0');
        if (foto) formData.append('foto', foto);

        setGuardando(true);
        setError('');
        try {
            const response = await actualizarRopa(formData);
            if (response?.success) {
                onSaved(response.data || []);
                onClose();
            } else {
                setError(response?.message || "No s'ha pogut actualitzar la roba.");
            }
        } catch (err) {
            console.error('Error en editar roba:', err);
            setError("No s'ha pogut actualitzar la roba.");
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Dialog open={Boolean(ropa)} onClose={cerrar} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Editar roba
                <IconButton aria-label="Tancar" onClick={cerrar} disabled={guardando}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                <Box className="clothes-form">
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField label="Nom" value={nombre} onChange={(event) => setNombre(event.target.value)} required fullWidth />
                    <Box className="clothes-form-color">
                        <Box className="clothes-form-color-copy">
                            <Typography className="clothes-form-label">Color</Typography>
                            <Typography className="clothes-form-code">{color}</Typography>
                        </Box>
                        <input type="color" aria-label="Color" value={color} onChange={(event) => setColor(event.target.value)} />
                    </Box>
                    {(preview || (ropa?.imagen && !quitarFoto)) && (
                        <Box className="clothes-edit-preview">
                            {preview ? <img src={preview} alt="Vista prèvia de la roba" /> : <ImagenPrivada path={ropa.imagen} alt={ropa.nombre} className="clothes-edit-image" wrapperClassName="clothes-edit-image-holder" />}
                        </Box>
                    )}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                            {foto ? foto.name : ropa?.imagen && !quitarFoto ? 'Canviar foto' : 'Afegir foto'}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                onChange={(event) => {
                                    setFoto(event.target.files?.[0] || null);
                                    setQuitarFoto(false);
                                }}
                            />
                        </Button>
                        {(foto || (ropa?.imagen && !quitarFoto)) && (
                            <Button
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => {
                                    setFoto(null);
                                    setQuitarFoto(true);
                                    if (fileInputRef.current) fileInputRef.current.value = '';
                                }}
                            >
                                Llevar foto
                            </Button>
                        )}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions className="clothes-dialog-actions">
                <Button onClick={cerrar} disabled={guardando}>Cancel·lar</Button>
                <Button variant="contained" onClick={guardar} disabled={guardando}>{guardando ? 'Guardant...' : 'Guardar'}</Button>
            </DialogActions>
        </Dialog>
    );
};
